import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Dumbbell, Plus, Search, Trash2, Edit2, X, Download, Upload, Link, Video, Loader2, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MarkdownEditor } from '@/components/ui/markdown-editor';
import { MarkdownRenderer } from '@/components/ui/markdown-renderer';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// NEW Drill Categories for Guided Training
const DRILL_CATEGORIES = [
  { value: 'WarmUp', label: 'Warm Up' },
  { value: 'Agility', label: 'Agility' },
  { value: 'Coordination', label: 'Coordination' },
  { value: 'Dribbling', label: 'Dribbling' },
  { value: 'Passing', label: 'Passing' },
  { value: 'FirstTouch', label: 'First Touch' },
  { value: 'Finishing', label: 'Finishing' },
  { value: 'Shooting', label: 'Shooting' },
  { value: 'Strength', label: 'Strength' },
  { value: 'Cooldown', label: 'Cool Down' }
];

// Legacy categories for backward compatibility
const CATEGORIES = ['technical', 'tactical', 'physical'];

// Skill Focus organized by category
const SKILL_CATEGORIES = {
  TECHNICAL: [
    { value: 'ball_control', label: 'Ball Control' },
    { value: 'passing', label: 'Passing' },
    { value: 'dribbling', label: 'Dribbling' },
    { value: 'first_touch', label: 'First Touch' },
    { value: 'finishing', label: 'Finishing' },
    { value: 'shooting', label: 'Shooting' },
  ],
  PHYSICAL: [
    { value: 'speed', label: 'Speed' },
    { value: 'acceleration', label: 'Acceleration' },
    { value: 'agility', label: 'Agility' },
    { value: 'coordination', label: 'Coordination' },
    { value: 'balance', label: 'Balance' },
    { value: 'explosiveness', label: 'Explosiveness' },
    { value: 'endurance', label: 'Endurance' },
  ],
  'TACTICAL / IQ': [
    { value: 'positioning', label: 'Positioning' },
    { value: 'decision_making', label: 'Decision Making' },
    { value: 'vision', label: 'Vision' },
    { value: 'game_awareness', label: 'Game Awareness' },
    { value: 'reaction_time', label: 'Reaction Time' },
  ],
};

// Flat list for filtering
const ALL_SKILLS = Object.values(SKILL_CATEGORIES).flat();

const DIFFICULTIES = ['Beginner', 'Intermediate', 'Advanced'];

// Skill Tags for drills
const SKILL_TAGS = [
  { value: 'BallControl', label: 'Ball Control' },
  { value: 'Passing', label: 'Passing' },
  { value: 'Dribbling', label: 'Dribbling' },
  { value: 'FirstTouch', label: 'First Touch' },
  { value: 'Finishing', label: 'Finishing' },
  { value: 'Shooting', label: 'Shooting' },
  { value: 'Speed', label: 'Speed' },
  { value: 'Acceleration', label: 'Acceleration' },
  { value: 'Agility', label: 'Agility' },
  { value: 'Coordination', label: 'Coordination' },
  { value: 'Balance', label: 'Balance' },
  { value: 'Explosiveness', label: 'Explosiveness' },
  { value: 'Endurance', label: 'Endurance' },
  { value: 'DecisionMaking', label: 'Decision Making' },
  { value: 'Positioning', label: 'Positioning' },
  { value: 'Vision', label: 'Vision' },
  { value: 'GameAwareness', label: 'Game Awareness' },
  { value: 'ReactionTime', label: 'Reaction Time' }
];

// Equipment Tags
const EQUIPMENT_TAGS = [
  { value: 'None', label: 'No Equipment' },
  { value: 'Ball', label: 'Ball' },
  { value: 'Cones', label: 'Cones' },
  { value: 'Wall', label: 'Wall' },
  { value: 'Goals', label: 'Goals' },
  { value: 'LadderOptional', label: 'Ladder (Optional)' }
];

// Space Tags
const SPACE_TAGS = [
  { value: 'SmallSpace', label: 'Small Space' },
  { value: 'Backyard', label: 'Backyard' },
  { value: 'Field', label: 'Field' }
];

const POSITIONS = {
  soccer: ['All', 'Goalkeeper', 'Defender', 'Midfielder', 'Forward', 'Winger']
};

const CoachDrills = () => {
  const { getAuthHeaders } = useAuth();
  const [drills, setDrills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterSport, setFilterSport] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterDifficulty, setFilterDifficulty] = useState('');
  const [filterSkill, setFilterSkill] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingDrill, setEditingDrill] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [seedingDefaults, setSeedingDefaults] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(null); // drill id being uploaded
  const [videoInputMode, setVideoInputMode] = useState('link'); // 'link' or 'upload'
  const [selectedVideoFile, setSelectedVideoFile] = useState(null);
  const [viewingDrill, setViewingDrill] = useState(null); // drill being viewed in detail
  const [filterHomeOnly, setFilterHomeOnly] = useState(false);
  const videoInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '', sport: 'soccer', category: 'Dribbling', skill_focus: 'ball_control',
    description: '', duration: '', difficulty: 'Intermediate', equipment: '', 
    video_url: '', position_focus: 'All',
    // New structured fields
    skill_tags: [],
    equipment_tags: [],
    space_tags: [],
    // Home Training fields
    is_home_drill: false,
    // Timer Configuration (REQUIRED for home drills)
    timer_type: 'Interval',  // 'Interval' or 'Continuous'
    // Interval Timer Fields
    sets: 3,
    work_seconds: 30,
    rest_seconds: 15,
    rest_between_sets_seconds: 0,
    // Continuous Timer Fields
    total_minutes: 3,
    // Legacy fields
    home_duration_minutes: 3,
    home_space: 'backyard',
    focus_tags: [],
    equipment_required: 'none'
  });

  // Timer validation
  const validateTimerConfig = () => {
    if (!formData.is_home_drill) return true;
    
    if (formData.timer_type === 'Interval') {
      if (!formData.sets || formData.sets < 1) return false;
      if (!formData.work_seconds || formData.work_seconds < 5) return false;
      if (formData.rest_seconds === undefined || formData.rest_seconds < 0) return false;
    } else if (formData.timer_type === 'Continuous') {
      if (!formData.total_minutes || formData.total_minutes < 1) return false;
    }
    return true;
  };

  const HOME_SPACES = [
    { value: 'small', label: 'Small Space' },
    { value: 'backyard', label: 'Backyard' },
    { value: 'field', label: 'Field' }
  ];

  const EQUIPMENT_OPTIONS = [
    { value: 'none', label: 'None' },
    { value: 'ball', label: 'Ball Only' },
    { value: 'cones', label: 'Cones' },
    { value: 'wall', label: 'Wall' }
  ];

  const FOCUS_TAGS = [
    'ball_control', 'passing', 'dribbling', 'first_touch',
    'speed_agility', 'coordination', 'balance', 'finishing'
  ];

  useEffect(() => {
    fetchDrills();
  }, []);

  const fetchDrills = async () => {
    try {
      const response = await axios.get(`${API}/drills`, { headers: getAuthHeaders() });
      setDrills(response.data);
    } catch (error) {
      toast.error('Failed to load drills');
    }
    setLoading(false);
  };

  const handleSeedHomeDrills = async () => {
    setSeedingDefaults(true);
    try {
      const res = await axios.post(`${API}/drills/seed-home-drills`, {}, { headers: getAuthHeaders() });
      if (res.data.count > 0) {
        toast.success(`${res.data.count} Home Training drills added!`);
        fetchDrills();
      } else {
        toast.info('Home drills already exist');
      }
    } catch (error) {
      toast.error('Failed to add home drills');
    }
    setSeedingDefaults(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate timer config for home drills
    if (formData.is_home_drill && !validateTimerConfig()) {
      toast.error('Please complete all timer configuration fields');
      return;
    }
    
    setSubmitting(true);
    try {
      let drillId = editingDrill?.id;
      
      if (editingDrill) {
        await axios.put(`${API}/drills/${editingDrill.id}`, formData, { headers: getAuthHeaders() });
        toast.success('Drill updated');
      } else {
        const response = await axios.post(`${API}/drills`, formData, { headers: getAuthHeaders() });
        drillId = response.data.id;
        toast.success('Drill added');
      }
      
      // If video file selected in upload mode, upload it
      if (videoInputMode === 'upload' && selectedVideoFile && drillId) {
        await handleVideoUpload(drillId, selectedVideoFile);
      }
      
      closeForm();
      fetchDrills();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save drill');
    }
    setSubmitting(false);
  };

  const handleVideoUpload = async (drillId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      setUploadingVideo(drillId);
      const response = await axios.post(
        `${API}/drills/${drillId}/upload-video`,
        formData,
        {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      toast.success(`Video uploaded (${response.data.size_mb}MB)`);
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload video');
      throw error;
    } finally {
      setUploadingVideo(null);
    }
  };

  const handleVideoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (100MB max)
      if (file.size > 100 * 1024 * 1024) {
        toast.error('Video file too large. Maximum size is 100MB');
        return;
      }
      // Validate file type
      const allowedTypes = ['video/mp4', 'video/quicktime', 'video/webm'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Invalid file type. Allowed: MP4, MOV, WebM');
        return;
      }
      setSelectedVideoFile(file);
      setFormData({...formData, video_url: ''}); // Clear URL when file selected
    }
  };

  const handleDeleteVideo = async (drillId) => {
    if (!window.confirm('Delete this video?')) return;
    try {
      await axios.delete(`${API}/drills/${drillId}/video`, { headers: getAuthHeaders() });
      toast.success('Video deleted');
      fetchDrills();
    } catch (error) {
      toast.error('Failed to delete video');
    }
  };

  const handleSeedDefaults = async () => {
    setSeedingDefaults(true);
    try {
      const response = await axios.post(`${API}/drills/seed-defaults`, {}, { headers: getAuthHeaders() });
      toast.success(`Added ${response.data.total_added} default drills`);
      fetchDrills();
    } catch (error) {
      toast.error('Failed to add default drills');
    }
    setSeedingDefaults(false);
  };

  const handleEdit = (drill) => {
    setEditingDrill(drill);
    setFormData({
      name: drill.name, 
      sport: drill.sport, 
      category: drill.category || 'Dribbling', 
      skill_focus: drill.skill_focus,
      description: drill.description, 
      duration: drill.duration, 
      difficulty: drill.difficulty || 'Intermediate',
      equipment: drill.equipment, 
      video_url: drill.video_url, 
      position_focus: drill.position_focus || 'All',
      // New structured fields
      skill_tags: drill.skill_tags || [],
      equipment_tags: drill.equipment_tags || [],
      space_tags: drill.space_tags || [],
      // Home Training fields
      is_home_drill: drill.is_home_drill || false,
      // Timer Configuration
      timer_type: drill.timer_type || 'Interval',
      sets: drill.sets || 3,
      work_seconds: drill.work_seconds || 30,
      rest_seconds: drill.rest_seconds || 15,
      rest_between_sets_seconds: drill.rest_between_sets_seconds || 0,
      total_minutes: drill.total_minutes || 3,
      // Legacy fields
      home_duration_minutes: drill.home_duration_minutes || 3,
      home_space: drill.home_space || 'backyard',
      focus_tags: drill.focus_tags || [],
      equipment_required: drill.equipment_required || 'none'
    });
    setShowForm(true);
  };

  const handleDelete = async (drillId) => {
    if (!window.confirm('Delete this drill?')) return;
    try {
      await axios.delete(`${API}/drills/${drillId}`, { headers: getAuthHeaders() });
      toast.success('Drill deleted');
      fetchDrills();
    } catch (error) {
      toast.error('Failed to delete drill');
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingDrill(null);
    setVideoInputMode('link');
    setSelectedVideoFile(null);
    setFormData({
      name: '', sport: 'soccer', category: 'Dribbling', skill_focus: 'ball_control',
      description: '', duration: '', difficulty: 'Intermediate', equipment: '', 
      video_url: '', position_focus: 'All',
      // New structured fields
      skill_tags: [],
      equipment_tags: [],
      space_tags: [],
      // Home Training fields
      is_home_drill: false,
      // Timer Configuration
      timer_type: 'Interval',
      sets: 3,
      work_seconds: 30,
      rest_seconds: 15,
      rest_between_sets_seconds: 0,
      total_minutes: 3,
      // Legacy fields
      home_duration_minutes: 3,
      home_space: 'backyard',
      focus_tags: [],
      equipment_required: 'none'
    });
  };

  const clearFilters = () => {
    setFilterSport('');
    setFilterCategory('');
    setFilterDifficulty('');
    setFilterSkill('');
    setFilterHomeOnly(false);
    setSearch('');
  };

  const filteredDrills = drills.filter(d => {
    const matchesSearch = d.name.toLowerCase().includes(search.toLowerCase()) ||
                          d.description?.toLowerCase().includes(search.toLowerCase());
    const matchesSport = !filterSport || d.sport === filterSport;
    const matchesCategory = !filterCategory || d.category === filterCategory;
    const matchesDifficulty = !filterDifficulty || d.difficulty === filterDifficulty;
    const matchesSkill = !filterSkill || d.skill_focus === filterSkill;
    const matchesHome = !filterHomeOnly || d.is_home_drill;
    return matchesSearch && matchesSport && matchesCategory && matchesDifficulty && matchesSkill && matchesHome;
  });

  const hasActiveFilters = filterSport || filterCategory || filterDifficulty || filterSkill || filterHomeOnly || search;
  const homeDrillsCount = drills.filter(d => d.is_home_drill).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="coach-drills">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl uppercase tracking-tight">Drill Library</h1>
          <p className="text-muted-foreground">{drills.length} drills in your library</p>
        </div>
        <div className="flex gap-2">
          {drills.length === 0 && (
            <Button 
              variant="outline" 
              onClick={handleSeedDefaults}
              disabled={seedingDefaults}
            >
              <Download className="mr-2 h-4 w-4" />
              {seedingDefaults ? 'Adding...' : 'Add Default Drills'}
            </Button>
          )}
          <Button className="bg-primary text-black hover:bg-primary/90" onClick={() => setShowForm(true)}>
            <Plus className="mr-2 h-4 w-4" />
            New Drill
          </Button>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
          <Input
            placeholder="Search drills..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-transparent"
          />
        </div>
        
        <div className="flex flex-wrap gap-2">
          <select
            value={filterSport}
            onChange={(e) => setFilterSport(e.target.value)}
            className="p-2 bg-transparent border border-border rounded-sm text-sm"
          >
            <option value="" className="bg-card">All Sports</option>
            <option value="soccer" className="bg-card">Soccer</option>
          </select>
          
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="p-2 bg-transparent border border-border rounded-sm text-sm"
          >
            <option value="" className="bg-card">All Categories</option>
            {CATEGORIES.map(cat => (
              <option key={cat} value={cat} className="bg-card capitalize">{cat}</option>
            ))}
          </select>
          
          <select
            value={filterDifficulty}
            onChange={(e) => setFilterDifficulty(e.target.value)}
            className="p-2 bg-transparent border border-border rounded-sm text-sm"
          >
            <option value="" className="bg-card">All Difficulties</option>
            {DIFFICULTIES.map(diff => (
              <option key={diff} value={diff} className="bg-card capitalize">{diff}</option>
            ))}
          </select>
          
          <select
            value={filterSkill}
            onChange={(e) => setFilterSkill(e.target.value)}
            className="p-2 bg-transparent border border-border rounded-sm text-sm"
          >
            <option value="" className="bg-card">All Skills</option>
            {Object.entries(SKILL_CATEGORIES).map(([category, skills]) => (
              <optgroup key={category} label={category} className="bg-card">
                {skills.map(skill => (
                  <option key={skill.value} value={skill.value} className="bg-card">
                    {skill.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>

          <button
            onClick={() => setFilterHomeOnly(!filterHomeOnly)}
            className={`px-3 py-2 text-sm rounded-sm border transition-colors ${
              filterHomeOnly 
                ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' 
                : 'bg-transparent border-border hover:border-emerald-500/50'
            }`}
          >
            🏠 Home Only
          </button>

          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X size={14} className="mr-1" /> Clear
            </Button>
          )}
        </div>
      </div>

      {/* Home Drills Stats & Actions */}
      <div className="flex flex-wrap items-center justify-between gap-4 bg-muted/20 p-3 rounded-sm">
        <div className="text-sm">
          <span className="text-muted-foreground">Home Training Drills:</span>
          <span className="ml-2 font-medium text-emerald-400">{homeDrillsCount}</span>
          <span className="text-muted-foreground ml-1">available for athletes</span>
        </div>
        {homeDrillsCount === 0 && (
          <Button 
            size="sm" 
            variant="outline" 
            onClick={handleSeedHomeDrills}
            disabled={seedingDefaults}
            className="border-emerald-500/30 hover:bg-emerald-500/10"
          >
            <Plus className="mr-2 h-4 w-4" />
            {seedingDefaults ? 'Adding...' : 'Add Home Drill Templates'}
          </Button>
        )}
      </div>

      {/* Add Default Drills Button (when empty after filters) */}
      {drills.length > 0 && filteredDrills.length === 0 && !hasActiveFilters && (
        <div className="bg-muted/30 p-4 rounded-sm text-center">
          <p className="text-muted-foreground mb-2">Want more drills?</p>
          <Button variant="outline" onClick={handleSeedDefaults} disabled={seedingDefaults}>
            <Download className="mr-2 h-4 w-4" />
            {seedingDefaults ? 'Adding...' : 'Add Default Drills'}
          </Button>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border p-6 rounded-sm w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl uppercase">{editingDrill ? 'Edit Drill' : 'New Drill'}</h2>
              <button onClick={closeForm} className="text-muted-foreground hover:text-foreground">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input placeholder="Drill Name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
              
              <div className="grid grid-cols-1 gap-4">
                {/* Drill Category for Guided Training Order */}
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Category (for Training Order)</label>
                  <select 
                    value={formData.category} 
                    onChange={(e) => setFormData({...formData, category: e.target.value})} 
                    className="w-full p-2 bg-transparent border border-border rounded-sm"
                  >
                    {DRILL_CATEGORIES.map(cat => (
                      <option key={cat.value} value={cat.value} className="bg-card">{cat.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <select 
                  value={formData.skill_focus} 
                  onChange={(e) => setFormData({...formData, skill_focus: e.target.value})} 
                  className="p-2 bg-transparent border border-border rounded-sm"
                >
                  {Object.entries(SKILL_CATEGORIES).map(([category, skills]) => (
                    <optgroup key={category} label={category} className="bg-card">
                      {skills.map(skill => (
                        <option key={skill.value} value={skill.value} className="bg-card">
                          {skill.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <select value={formData.difficulty} onChange={(e) => setFormData({...formData, difficulty: e.target.value})} className="p-2 bg-transparent border border-border rounded-sm">
                  {DIFFICULTIES.map(diff => (<option key={diff} value={diff} className="bg-card capitalize">{diff}</option>))}
                </select>
              </div>

              <select value={formData.position_focus} onChange={(e) => setFormData({...formData, position_focus: e.target.value})} className="w-full p-2 bg-transparent border border-border rounded-sm">
                {POSITIONS['soccer'].map(pos => (<option key={pos} value={pos} className="bg-card">{pos}</option>))}
              </select>

              <MarkdownEditor 
                value={formData.description} 
                onChange={(value) => setFormData({...formData, description: value})} 
                placeholder="Describe the drill methodology, setup, execution steps, coaching points..."
                minRows={8}
                data-testid="drill-description-editor"
              />
              
              <div className="grid grid-cols-2 gap-4">
                <Input placeholder="Duration (e.g., 15 min)" value={formData.duration} onChange={(e) => setFormData({...formData, duration: e.target.value})} />
                <Input placeholder="Equipment needed" value={formData.equipment} onChange={(e) => setFormData({...formData, equipment: e.target.value})} />
              </div>
              
              {/* Home Training Section */}
              <div className="border border-border/40 rounded-sm p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_home_drill"
                    checked={formData.is_home_drill}
                    onChange={(e) => setFormData({...formData, is_home_drill: e.target.checked})}
                    className="rounded"
                  />
                  <label htmlFor="is_home_drill" className="text-sm font-medium">Enable for Home Training</label>
                </div>
                
                {formData.is_home_drill && (
                  <div className="space-y-4 pl-6 border-l-2 border-primary/30">
                    {/* Space Tags */}
                    <div>
                      <label className="text-xs text-muted-foreground block mb-2">Available Spaces</label>
                      <div className="flex flex-wrap gap-2">
                        {SPACE_TAGS.map(space => (
                          <button
                            key={space.value}
                            type="button"
                            onClick={() => {
                              const newSpaces = formData.space_tags.includes(space.value)
                                ? formData.space_tags.filter(s => s !== space.value)
                                : [...formData.space_tags, space.value];
                              setFormData({...formData, space_tags: newSpaces});
                            }}
                            className={`px-3 py-1.5 text-xs rounded-sm ${
                              formData.space_tags.includes(space.value)
                                ? 'bg-primary text-black'
                                : 'bg-muted hover:bg-muted/80'
                            }`}
                          >
                            {space.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Equipment Tags */}
                    <div>
                      <label className="text-xs text-muted-foreground block mb-2">Equipment Required</label>
                      <div className="flex flex-wrap gap-2">
                        {EQUIPMENT_TAGS.map(equip => (
                          <button
                            key={equip.value}
                            type="button"
                            onClick={() => {
                              const newEquip = formData.equipment_tags.includes(equip.value)
                                ? formData.equipment_tags.filter(e => e !== equip.value)
                                : [...formData.equipment_tags, equip.value];
                              setFormData({...formData, equipment_tags: newEquip});
                            }}
                            className={`px-3 py-1.5 text-xs rounded-sm ${
                              formData.equipment_tags.includes(equip.value)
                                ? 'bg-blue-500 text-white'
                                : 'bg-muted hover:bg-muted/80'
                            }`}
                          >
                            {equip.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Skill Tags */}
                    <div>
                      <label className="text-xs text-muted-foreground block mb-2">Skill Focus Tags</label>
                      <div className="flex flex-wrap gap-1">
                        {SKILL_TAGS.slice(0, 10).map(skill => (
                          <button
                            key={skill.value}
                            type="button"
                            onClick={() => {
                              const newSkills = formData.skill_tags.includes(skill.value)
                                ? formData.skill_tags.filter(s => s !== skill.value)
                                : [...formData.skill_tags, skill.value];
                              setFormData({...formData, skill_tags: newSkills});
                            }}
                            className={`px-2 py-0.5 text-xs rounded-sm ${
                              formData.skill_tags.includes(skill.value)
                                ? 'bg-emerald-500 text-black'
                                : 'bg-muted hover:bg-muted/80'
                            }`}
                          >
                            {skill.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* TIMER SETUP SECTION */}
                    <div className="border-t border-border/40 pt-4">
                      <h4 className="text-sm font-semibold uppercase tracking-wider text-primary mb-3">Timer Setup</h4>
                      
                      {/* Timer Type Selector */}
                      <div className="flex gap-2 mb-4">
                        <button
                          type="button"
                          onClick={() => setFormData({...formData, timer_type: 'Interval'})}
                          className={`flex-1 py-2 px-4 text-sm rounded-sm border transition-colors ${
                            formData.timer_type === 'Interval'
                              ? 'bg-primary text-black border-primary'
                              : 'bg-transparent border-border hover:border-primary/50'
                          }`}
                        >
                          Interval (Sets)
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData({...formData, timer_type: 'Continuous'})}
                          className={`flex-1 py-2 px-4 text-sm rounded-sm border transition-colors ${
                            formData.timer_type === 'Continuous'
                              ? 'bg-primary text-black border-primary'
                              : 'bg-transparent border-border hover:border-primary/50'
                          }`}
                        >
                          Continuous
                        </button>
                      </div>

                      {/* Interval Timer Fields */}
                      {formData.timer_type === 'Interval' && (
                        <div className="space-y-3 bg-muted/20 p-3 rounded-sm">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs text-muted-foreground block mb-1">Sets *</label>
                              <Input
                                type="number"
                                min="1"
                                max="20"
                                value={formData.sets}
                                onChange={(e) => setFormData({...formData, sets: parseInt(e.target.value) || 1})}
                                required
                              />
                            </div>
                            <div>
                              <label className="text-xs text-muted-foreground block mb-1">Work (seconds) *</label>
                              <Input
                                type="number"
                                min="5"
                                max="300"
                                value={formData.work_seconds}
                                onChange={(e) => setFormData({...formData, work_seconds: parseInt(e.target.value) || 30})}
                                required
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs text-muted-foreground block mb-1">Rest (seconds) *</label>
                              <Input
                                type="number"
                                min="0"
                                max="120"
                                value={formData.rest_seconds}
                                onChange={(e) => setFormData({...formData, rest_seconds: parseInt(e.target.value) || 0})}
                                required
                              />
                            </div>
                            <div>
                              <label className="text-xs text-muted-foreground block mb-1">Rest Between Sets (sec)</label>
                              <Input
                                type="number"
                                min="0"
                                max="180"
                                value={formData.rest_between_sets_seconds}
                                onChange={(e) => setFormData({...formData, rest_between_sets_seconds: parseInt(e.target.value) || 0})}
                              />
                            </div>
                          </div>
                          <div className="text-xs text-muted-foreground mt-2">
                            Total: ~{Math.ceil(((formData.work_seconds + formData.rest_seconds) * formData.sets + (formData.rest_between_sets_seconds || 0) * (formData.sets - 1)) / 60)} min
                          </div>
                        </div>
                      )}

                      {/* Continuous Timer Fields */}
                      {formData.timer_type === 'Continuous' && (
                        <div className="space-y-3 bg-muted/20 p-3 rounded-sm">
                          <div>
                            <label className="text-xs text-muted-foreground block mb-1">Total Duration (minutes) *</label>
                            <Input
                              type="number"
                              min="1"
                              max="60"
                              value={formData.total_minutes}
                              onChange={(e) => setFormData({...formData, total_minutes: parseInt(e.target.value) || 3})}
                              required
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Video Input Section */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <label className="text-sm text-muted-foreground">Video (optional)</label>
                  <div className="flex gap-1 ml-auto">
                    <button
                      type="button"
                      onClick={() => { setVideoInputMode('link'); setSelectedVideoFile(null); }}
                      className={`px-3 py-1 text-xs rounded-sm flex items-center gap-1 ${videoInputMode === 'link' ? 'bg-primary text-black' : 'bg-muted'}`}
                    >
                      <Link size={12} /> Link
                    </button>
                    <button
                      type="button"
                      onClick={() => { setVideoInputMode('upload'); setFormData({...formData, video_url: ''}); }}
                      className={`px-3 py-1 text-xs rounded-sm flex items-center gap-1 ${videoInputMode === 'upload' ? 'bg-primary text-black' : 'bg-muted'}`}
                    >
                      <Upload size={12} /> Upload
                    </button>
                  </div>
                </div>
                
                {videoInputMode === 'link' ? (
                  <Input 
                    placeholder="Video URL (YouTube, Vimeo, etc.)" 
                    value={formData.video_url} 
                    onChange={(e) => setFormData({...formData, video_url: e.target.value})} 
                  />
                ) : (
                  <div className="space-y-2">
                    <input
                      ref={videoInputRef}
                      type="file"
                      accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm"
                      onChange={handleVideoFileChange}
                      className="hidden"
                    />
                    <div 
                      onClick={() => videoInputRef.current?.click()}
                      className="border border-dashed border-border rounded-sm p-4 text-center cursor-pointer hover:border-primary/50 transition-colors"
                    >
                      {selectedVideoFile ? (
                        <div className="flex items-center justify-center gap-2">
                          <Video size={16} className="text-primary" />
                          <span className="text-sm">{selectedVideoFile.name}</span>
                          <span className="text-xs text-muted-foreground">
                            ({(selectedVideoFile.size / (1024*1024)).toFixed(1)}MB)
                          </span>
                        </div>
                      ) : (
                        <div className="text-muted-foreground">
                          <Upload size={20} className="mx-auto mb-1" />
                          <p className="text-sm">Click to select video</p>
                          <p className="text-xs">MP4, MOV, WebM up to 100MB</p>
                        </div>
                      )}
                    </div>
                    {selectedVideoFile && (
                      <button
                        type="button"
                        onClick={() => setSelectedVideoFile(null)}
                        className="text-xs text-red-400 hover:underline"
                      >
                        Remove selected file
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={closeForm} className="flex-1">Cancel</Button>
                <Button type="submit" disabled={submitting || uploadingVideo} className="flex-1 bg-primary text-black">
                  {submitting ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...</>
                  ) : editingDrill ? 'Update' : 'Add Drill'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Drills Grid */}
      {filteredDrills.length === 0 ? (
        <div className="bg-card border border-border/40 p-12 rounded-sm text-center">
          <Dumbbell className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground mb-4">{hasActiveFilters ? 'No drills match your filters' : 'No drills in your library yet'}</p>
          {!hasActiveFilters && (
            <div className="flex gap-2 justify-center">
              <Button variant="outline" onClick={handleSeedDefaults} disabled={seedingDefaults}>
                <Download className="mr-2 h-4 w-4" />
                Add Default Drills
              </Button>
              <Button onClick={() => setShowForm(true)}>Create Custom Drill</Button>
            </div>
          )}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredDrills.map((drill) => (
            <div key={drill.id} className="bg-card border border-border/40 p-4 rounded-sm relative">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-medium">{drill.name}</h3>
                    {drill.is_home_drill && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 rounded uppercase">Home</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded capitalize">{drill.sport}</span>
                    <span className="text-xs px-2 py-0.5 bg-muted rounded capitalize">{drill.category}</span>
                    <span className="text-xs px-2 py-0.5 bg-muted rounded capitalize">{drill.difficulty}</span>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setViewingDrill(drill)} className="p-1.5 hover:bg-muted rounded-sm" title="View details">
                    <Eye size={14} />
                  </button>
                  <button onClick={() => handleEdit(drill)} className="p-1.5 hover:bg-muted rounded-sm">
                    <Edit2 size={14} />
                  </button>
                  <button onClick={() => handleDelete(drill.id)} className="p-1.5 hover:bg-muted rounded-sm text-red-400">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <MarkdownRenderer content={drill.description} truncate maxLines={2} className="text-sm mb-2" />
              <div className="text-xs text-muted-foreground space-y-1">
                <p><span className="text-foreground">Skill:</span> {drill.skill_focus?.replace('_', ' ')}</p>
                {drill.duration && <p><span className="text-foreground">Duration:</span> {drill.duration}</p>}
                {drill.equipment && <p><span className="text-foreground">Equipment:</span> {drill.equipment}</p>}
                {drill.position_focus && drill.position_focus !== 'All' && (
                  <p><span className="text-foreground">Position:</span> {drill.position_focus}</p>
                )}
              </div>
              {drill.video_url && (
                <div className="mt-2 flex items-center gap-2">
                  {drill.video_url.includes('/uploads/videos/') ? (
                    <>
                      <video 
                        src={`${BACKEND_URL}${drill.video_url}`} 
                        className="w-full h-24 object-cover rounded-sm" 
                        controls
                      />
                      <button 
                        onClick={() => handleDeleteVideo(drill.id)}
                        className="absolute top-2 right-2 p-1 bg-red-500/80 rounded text-white hover:bg-red-600"
                        title="Delete video"
                      >
                        <Trash2 size={12} />
                      </button>
                    </>
                  ) : (
                    <a href={drill.video_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1">
                      <Video size={12} /> Watch Video
                    </a>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* View Drill Detail Modal */}
      {viewingDrill && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border p-6 rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="font-heading text-2xl uppercase">{viewingDrill.name}</h2>
                <div className="flex flex-wrap gap-2 mt-2">
                  <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded capitalize">{viewingDrill.sport}</span>
                  <span className="text-xs px-2 py-1 bg-muted rounded capitalize">{viewingDrill.category}</span>
                  <span className="text-xs px-2 py-1 bg-muted rounded capitalize">{viewingDrill.difficulty}</span>
                  <span className="text-xs px-2 py-1 bg-muted rounded capitalize">{viewingDrill.skill_focus?.replace('_', ' ')}</span>
                </div>
              </div>
              <button onClick={() => setViewingDrill(null)} className="text-muted-foreground hover:text-foreground">
                <X size={24} />
              </button>
            </div>

            {/* Drill Info */}
            <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
              {viewingDrill.duration && (
                <div>
                  <span className="text-muted-foreground">Duration:</span>{' '}
                  <span className="text-foreground">{viewingDrill.duration}</span>
                </div>
              )}
              {viewingDrill.equipment && (
                <div>
                  <span className="text-muted-foreground">Equipment:</span>{' '}
                  <span className="text-foreground">{viewingDrill.equipment}</span>
                </div>
              )}
              {viewingDrill.position_focus && viewingDrill.position_focus !== 'All' && (
                <div>
                  <span className="text-muted-foreground">Position Focus:</span>{' '}
                  <span className="text-foreground">{viewingDrill.position_focus}</span>
                </div>
              )}
            </div>

            {/* Description with Markdown Rendering */}
            <div className="mb-6">
              <h3 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">Description</h3>
              <div className="bg-muted/30 p-4 rounded-sm border border-border/40">
                <MarkdownRenderer content={viewingDrill.description} />
              </div>
            </div>

            {/* Video */}
            {viewingDrill.video_url && (
              <div className="mb-6">
                <h3 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">Video</h3>
                {viewingDrill.video_url.includes('/uploads/videos/') ? (
                  <video 
                    src={`${BACKEND_URL}${viewingDrill.video_url}`} 
                    className="w-full rounded-sm" 
                    controls
                  />
                ) : (
                  <a 
                    href={viewingDrill.video_url} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="inline-flex items-center gap-2 text-primary hover:underline"
                  >
                    <Video size={16} /> Watch Video
                  </a>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 pt-4 border-t border-border">
              <Button variant="outline" onClick={() => setViewingDrill(null)} className="flex-1">
                Close
              </Button>
              <Button 
                onClick={() => { handleEdit(viewingDrill); setViewingDrill(null); }} 
                className="flex-1 bg-primary text-black"
              >
                <Edit2 size={16} className="mr-2" /> Edit Drill
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoachDrills;
